import { useEffect, useState } from "react";
import { Loader2, UserPlus, LogOut, Mail, Users, Save, Search, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImageUpload from "@/components/ImageUpload";

export default function GuestsPage() {
  const [guests, setGuests] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [biz, setBiz] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", room: "", check_in: "", check_out: "" });
  const [preview, setPreview] = useState(null);
  const [folio, setFolio] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const load = () => Promise.all([api.get("/guests"), api.get("/email-logs"), api.get("/business")]).then(([g, l, b]) => {
    setGuests(g.data); setLogs(l.data); setBiz(b.data); setLoading(false);
  });
  useEffect(() => { load(); }, []);

  const checkIn = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      const { data } = await api.post("/guests", { ...form, origin: window.location.origin });
      setPreview(data);
      setOpen(false);
      setForm({ name: "", email: "", room: "", check_in: "", check_out: "" });
      toast.success(data.email_status === "sent" ? "Guest checked in — welcome email sent" : data.email_status === "failed" ? "Guest checked in — email send failed (see Sent tab)" : "Guest checked in — welcome email prepared");
      load();
    } catch (e) { toast.error("Could not check in guest"); }
  };

  const checkOut = async (id) => { await api.put(`/guests/${id}`, { status: "checked_out" }); toast.success("Checked out"); load(); };

  const confirmDeleteGuest = async () => {
    const g = deleteTarget; setDeleteTarget(null);
    try { await api.delete(`/guests/${g.id}`); toast.success("Guest deleted"); load(); }
    catch { toast.error("Could not delete guest"); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const bulkCheckout = async () => {
    try { await api.post("/guests/bulk", { ids: selectedIds, action: "checkout" }); toast.success("Checked out selected"); setSelected({}); load(); }
    catch { toast.error("Bulk action failed"); }
  };
  const bulkDelete = async () => {
    setBulkConfirm(false);
    try { const { data } = await api.post("/guests/bulk", { ids: selectedIds, action: "delete" }); toast.success(`Deleted ${data.count} guest(s)`); setSelected({}); load(); }
    catch { toast.error("Bulk delete failed"); }
  };

  const openFolio = async (g) => {
    setFolio({ guest: g, loading: true });
    try { const { data } = await api.get(`/guests/${g.id}/folio`); setFolio({ guest: g, ...data, loading: false }); }
    catch { setFolio({ guest: g, orders: [], total: 0, loading: false }); }
  };

  const settle = async () => {
    await api.post(`/guests/${folio.guest.id}/settle`);
    toast.success("Folio settled & guest checked out");
    setFolio(null);
    load();
  };

  const saveTpl = async () => {
    setSavingTpl(true);
    try {
      await api.put("/business", {
        welcome_email_subject: biz.welcome_email_subject, welcome_email_intro: biz.welcome_email_intro,
        welcome_email_image: biz.welcome_email_image, welcome_email_signoff: biz.welcome_email_signoff,
      });
      toast.success("Template saved");
    } catch { toast.error("Could not save"); }
    finally { setSavingTpl(false); }
  };

  const q = search.trim().toLowerCase();
  const match = (g) => !q || [g.name, g.email, g.room].some((v) => (v || "").toLowerCase().includes(q));
  const activeGuests = guests.filter((g) => g.status !== "checked_out" && match(g));
  const archivedGuests = guests.filter((g) => g.status === "checked_out" && match(g));

  const GuestList = ({ list, empty }) => list.length === 0 ? (
    <div className="text-center py-16 border border-dashed border-border rounded-md"><Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">{empty}</p></div>
  ) : (
    <div className="bg-card border border-border rounded-md divide-y divide-border">
      {list.map((g) => (
        <div key={g.id} className="p-4 flex items-center justify-between gap-4" data-testid={`guest-${g.id}`}>
          <input type="checkbox" checked={!!selected[g.id]} onChange={() => toggleSel(g.id)} data-testid={`select-guest-${g.id}`} className="w-4 h-4 accent-[#1F3A34] shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-foreground font-medium">{g.name}</p>
              <Badge className={`${g.status === "checked_out" ? "bg-secondary text-muted-foreground" : "bg-green-100 text-green-800"} border-0 capitalize`}>{(g.status || "checked_in").replace("_", " ")}</Badge>
              {!g.has_password && <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">Password not set</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{g.email} · Room {g.room || "—"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">In: {g.check_in || "—"} · Out: {g.check_out || "—"}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-serif text-lg text-foreground" data-testid={`folio-${g.id}`}>£{g.folio_total?.toFixed?.(2) ?? g.folio_total}</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Room folio</p>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => openFolio(g)} data-testid={`view-folio-${g.id}`}>View folio</Button>
              {g.status !== "checked_out" && <Button size="sm" variant="outline" onClick={() => checkOut(g.id)} data-testid={`checkout-${g.id}`}><LogOut className="w-3.5 h-3.5 mr-1" /> Check out</Button>}
              <Button size="sm" variant="outline" onClick={() => setDeleteTarget(g)} className="text-red-600 hover:text-red-700" data-testid={`delete-guest-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const SearchBar = ({ testId }) => (
    <div className="relative max-w-sm">
      <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or room…" className="pl-9" data-testid={testId} />
    </div>
  );

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;

  return (
    <div className="space-y-6" data-testid="guests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Guests & Check-in</h1>
          <p className="text-muted-foreground text-sm">Check guests in, send the welcome email, and manage the email template.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="checkin-guest-btn" className="bg-primary text-primary-foreground"><UserPlus className="w-4 h-4 mr-2" /> Check in guest</Button>
      </div>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active"><Users className="w-4 h-4 mr-2" /> Active ({activeGuests.length})</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">Archived ({archivedGuests.length})</TabsTrigger>
          <TabsTrigger value="template" data-testid="tab-template"><Mail className="w-4 h-4 mr-2" /> Email Template</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Sent ({logs.length})</TabsTrigger>
        </TabsList>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-3 bg-secondary/60 rounded-md p-3 mt-4" data-testid="bulk-bar">
            <span className="text-sm text-foreground">{selectedIds.length} selected</span>
            <Button size="sm" variant="outline" onClick={bulkCheckout} data-testid="bulk-checkout-btn"><LogOut className="w-3.5 h-3.5 mr-1" /> Check out</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm(true)} className="text-red-600" data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected({})} data-testid="bulk-clear-btn">Clear</Button>
          </div>
        )}

        <TabsContent value="active" className="mt-4 space-y-4">
          <SearchBar testId="guest-search-active" />
          <GuestList list={activeGuests} empty={q ? "No active guests match your search." : "No active guests."} />
        </TabsContent>

        <TabsContent value="archived" className="mt-4 space-y-4">
          <SearchBar testId="guest-search-archived" />
          <GuestList list={archivedGuests} empty={q ? "No checked-out guests match your search." : "No checked-out guests yet."} />
        </TabsContent>

        <TabsContent value="template" className="mt-4">
          <div className="bg-card border border-border rounded-md p-6 space-y-4 max-w-2xl">
            <div><Label>Subject</Label><Input data-testid="tpl-subject" value={biz.welcome_email_subject || ""} onChange={(e) => setBiz({ ...biz, welcome_email_subject: e.target.value })} className="mt-1.5" placeholder={`Welcome to ${biz.name}`} /></div>
            <div><Label>Intro paragraph</Label><Textarea data-testid="tpl-intro" value={biz.welcome_email_intro || ""} onChange={(e) => setBiz({ ...biz, welcome_email_intro: e.target.value })} rows={3} className="mt-1.5" placeholder="We can't wait to welcome you…" /></div>
            <div><Label>Sign-off</Label><Input data-testid="tpl-signoff" value={biz.welcome_email_signoff || ""} onChange={(e) => setBiz({ ...biz, welcome_email_signoff: e.target.value })} className="mt-1.5" placeholder={`Warm regards, ${biz.name}`} /></div>
            <div><Label className="mb-1.5 block">Header image</Label><ImageUpload value={biz.welcome_email_image} onChange={(v) => setBiz({ ...biz, welcome_email_image: v })} testId="tpl-image" label="header" /></div>
            <p className="text-xs text-muted-foreground">Check-in/out dates, room number and a secure set-password link are added automatically per guest.</p>
            <Button onClick={saveTpl} disabled={savingTpl} data-testid="save-tpl-btn" className="bg-primary text-primary-foreground">{savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save template</>}</Button>
          </div>
        </TabsContent>

        <TabsContent value="sent" className="mt-4">
          {logs.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md"><Mail className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No emails sent yet.</p></div>
          ) : (
            <div className="space-y-3">
              {logs.map((l) => (
                <div key={l.id} className="bg-card border border-border rounded-md p-4" data-testid={`emaillog-${l.id}`}>
                  <div className="flex items-center justify-between"><p className="font-medium text-foreground">{l.subject}</p><Badge className={`text-xs border-0 ${l.status === "sent" ? "bg-green-100 text-green-800" : l.status === "failed" ? "bg-red-100 text-red-700" : "bg-secondary text-muted-foreground"}`} data-testid={`emaillog-status-${l.id}`}>{(l.status || "logged").toUpperCase()}{l.smtp_source ? ` · ${l.smtp_source}` : ""}</Badge></div>
                  <p className="text-xs text-muted-foreground mt-0.5">To: {l.to} · {new Date(l.created_at).toLocaleString()}</p>
                  {l.status === "failed" && l.error && <p className="text-xs text-red-600 mt-1">Send error: {l.error}</p>}
                  <pre className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap font-sans">{l.body}</pre>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Check-in dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Check in a guest</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Guest name</Label><Input data-testid="ci-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="ci-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Room</Label><Input data-testid="ci-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Check-in</Label><Input data-testid="ci-checkin" type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Check-out</Label><Input data-testid="ci-checkout" type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className="mt-1.5" /></div>
            </div>
            <Button onClick={checkIn} data-testid="submit-checkin-btn" className="w-full bg-primary text-primary-foreground">Check in & send welcome email</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome email preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md" data-testid="welcome-preview">
          <DialogHeader><DialogTitle className="font-serif text-xl">Welcome email</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3">
              {preview.email_status && <Badge className={`border-0 ${preview.email_status === "sent" ? "bg-green-100 text-green-800" : preview.email_status === "failed" ? "bg-red-100 text-red-700" : "bg-secondary text-muted-foreground"}`} data-testid="preview-email-status">{preview.email_status === "sent" ? "Email sent" : preview.email_status === "failed" ? "Send failed — no SMTP or error" : "Logged (no SMTP configured)"}</Badge>}
              <p className="text-sm"><span className="text-muted-foreground">Subject:</span> {preview.subject}</p>
              <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-sans border border-border rounded-md p-3 max-h-72 overflow-y-auto">{preview.body}</pre>
              <div className="bg-secondary rounded-md p-3">
                <p className="text-xs text-muted-foreground mb-1">Set-password link (share with guest):</p>
                <code className="text-xs break-all text-accent" data-testid="setpw-link">{window.location.origin}{preview.setpw_url}</code>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Folio dialog */}
      <Dialog open={!!folio} onOpenChange={(o) => !o && setFolio(null)}>
        <DialogContent className="max-w-md" data-testid="folio-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Room folio — {folio?.guest?.name}</DialogTitle></DialogHeader>
          {folio?.loading ? <Loader2 className="w-5 h-5 animate-spin text-accent mx-auto my-6" /> : (
            <div className="space-y-3">
              {(folio?.orders || []).length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No charges on this folio.</p> : (
                <div className="border border-border rounded-md divide-y divide-border max-h-72 overflow-y-auto">
                  {folio.orders.map((o) => (
                    <div key={o.id} className="p-3 text-sm">
                      <div className="flex justify-between"><span className="text-foreground">{(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}</span><span className="font-medium">£{o.total}</span></div>
                      <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.settled ? "settled" : o.status}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between items-center border-t border-border pt-3">
                <span className="text-muted-foreground text-sm">Outstanding total</span>
                <span className="font-serif text-2xl text-foreground" data-testid="folio-total">£{(folio?.total || 0).toFixed(2)}</span>
              </div>
              <Button onClick={settle} data-testid="settle-folio-btn" className="w-full bg-primary text-primary-foreground">Settle & check out</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-guest-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Delete {deleteTarget?.name}?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">This permanently removes the guest and their orders, bookings and messages. This cannot be undone.</DialogDescription>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-guest-cancel">Cancel</Button>
            <Button onClick={confirmDeleteGuest} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-guest-confirm">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <DialogContent className="max-w-sm" data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Delete {selectedIds.length} guests?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">This permanently removes the selected guests and their orders, bookings and messages.</DialogDescription>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setBulkConfirm(false)}>Cancel</Button>
            <Button onClick={bulkDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-delete-confirm">Delete all</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
